import React, { createContext, useContext, useState, ReactNode } from 'react';

interface AdvancedAnalysisSettings {
  // Delta thresholds for OTM/ATM/ITM classification
  otmDeltaThreshold: number; // Default: 0.3
  atmDeltaThreshold: number; // Default: 0.7
  // Global moneyness filter
  globalMoneynessFilter: 'OTM' | 'ATM' | 'ITM' | 'ALL';
  // Scenario assignment settings
  enableScenarioAssignment: boolean;
  // Belt ranges analysis settings
  beltAnalysisThreshold: number; // Default: 0.5
  // Dynamic band detection settings
  enableDynamicBandDetection: boolean;
  // Moneyness filter range
  moneynessFilterRange: {
    minStrike: number;
    maxStrike: number;
    selectedMoneyness: 'OTM' | 'ATM' | 'ITM' | 'ALL';
  };
  // New filters
  selectedUnderlying: string; // Default: 'all'
  selectedExpiry: string; // Default: 'all'
  // New moneyness logic (numeric moneyness from -50 to 50)
  moneynessMargin: number; // Default: 10 (range from -10 to 10)
  selectedMoneynessType: 'ALL' | 'CALLS' | 'PUTS'; // Default: 'ALL'
}

interface AdvancedAnalysisContextType {
  settings: AdvancedAnalysisSettings;
  updateSettings: (newSettings: Partial<AdvancedAnalysisSettings>) => void;
  resetSettings: () => void;
}

const defaultSettings: AdvancedAnalysisSettings = {
  otmDeltaThreshold: 0.3,
  atmDeltaThreshold: 0.7,
  globalMoneynessFilter: 'ALL',
  enableScenarioAssignment: true,
  beltAnalysisThreshold: 0.5,
  enableDynamicBandDetection: true,
  moneynessFilterRange: {
    minStrike: 0.8,
    maxStrike: 1.2,
    selectedMoneyness: 'ALL',
  },
  selectedUnderlying: 'all',
  selectedExpiry: 'all',
  moneynessMargin: 10,
  selectedMoneynessType: 'ALL',
};

const AdvancedAnalysisContext = createContext<AdvancedAnalysisContextType | undefined>(undefined);

export const useAdvancedAnalysis = () => {
  const context = useContext(AdvancedAnalysisContext);
  if (context === undefined) {
    throw new Error('useAdvancedAnalysis must be used within an AdvancedAnalysisProvider');
  }
  return context;
};

interface AdvancedAnalysisProviderProps {
  children: ReactNode;
}

export const AdvancedAnalysisProvider: React.FC<AdvancedAnalysisProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<AdvancedAnalysisSettings>(defaultSettings);

  const updateSettings = (newSettings: Partial<AdvancedAnalysisSettings>) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
  };

  return (
    <AdvancedAnalysisContext.Provider value={{ settings, updateSettings, resetSettings }}>
      {children}
    </AdvancedAnalysisContext.Provider>
  );
}; 